import { useState, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useDebounce } from '@/lib/hooks/useDebounce';
import type { ListViewColumnConfig } from '@/lib/views/listViewSchemas/types';
import type { FilterOperator, FilterValue } from '@/lib/erp/filterTypes';
import type { AdvancedFilterRow as AdvancedFilterRowData } from './useAdvancedTableState';
import {
  getFilterVariant,
  getOperatorsForVariant,
  NO_VALUE_OPERATORS,
  DUAL_VALUE_OPERATORS,
} from './advancedTableConfig';
import { DEFAULT_ADVANCED_TABLE_LABELS, type AdvancedTableLabels } from './advancedTableLabels';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AdvancedFilterRowProps {
  row: AdvancedFilterRowData;
  index: number;
  columns: ListViewColumnConfig[];
  onChange: (id: string, updates: Partial<AdvancedFilterRowData>) => void;
  onRemove: (id: string) => void;
  labels?: Partial<AdvancedTableLabels>;
  /** Pre-fetched select options per fieldname */
  selectOptions?: Record<string, { label: string; value: string }[]>;
}

// ---------------------------------------------------------------------------
// Debounced text/number input
// ---------------------------------------------------------------------------

function DebouncedInput({
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  value: string;
  onChange: (val: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
}) {
  const [localValue, setLocalValue] = useState(value);
  const debouncedValue = useDebounce(localValue, 300);

  useEffect(() => {
    if (debouncedValue !== value) {
      onChange(debouncedValue);
    }
  }, [debouncedValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync external changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <Input
      type={type}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      placeholder={placeholder ?? (type === 'number' ? '0' : 'Value...')}
      className="h-8 w-[140px]"
    />
  );
}

// ---------------------------------------------------------------------------
// Select value input with Command search
// ---------------------------------------------------------------------------

function SelectValueInput({
  value,
  options,
  onChange,
  placeholder,
}: {
  value: string;
  options: { label: string; value: string }[];
  onChange: (val: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-8 w-[160px] justify-between text-xs font-normal"
        >
          <span className="truncate">{selected?.label ?? placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput placeholder={placeholder} className="h-8" />
          <CommandList>
            <CommandEmpty>{placeholder}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-3 w-3',
                      value === opt.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Value input renderer per variant
// ---------------------------------------------------------------------------

function ValueInput({
  row,
  column,
  onChange,
  labels,
  selectOptions,
}: {
  row: AdvancedFilterRowData;
  column: ListViewColumnConfig | undefined;
  onChange: (value: FilterValue) => void;
  labels: AdvancedTableLabels;
  selectOptions?: { label: string; value: string }[];
}) {
  const variant = getFilterVariant(column?.type);

  if (NO_VALUE_OPERATORS.includes(row.operator)) {
    return null;
  }

  if (DUAL_VALUE_OPERATORS.includes(row.operator)) {
    const arr = Array.isArray(row.value) ? row.value : ['', ''];
    return (
      <div className="flex items-center gap-1">
        <DebouncedInput
          value={String(arr[0] ?? '')}
          onChange={(v) => onChange([v, String(arr[1] ?? '')])}
          type={variant === 'number' ? 'number' : 'text'}
          placeholder={labels.filterMin}
        />
        <span className="text-xs text-muted-foreground">—</span>
        <DebouncedInput
          value={String(arr[1] ?? '')}
          onChange={(v) => onChange([String(arr[0] ?? ''), v])}
          type={variant === 'number' ? 'number' : 'text'}
          placeholder={labels.filterMax}
        />
      </div>
    );
  }

  if (variant === 'boolean') {
    return (
      <Select
        value={row.value != null ? String(row.value) : ''}
        onValueChange={(v) => onChange(v === '1')}
      >
        <SelectTrigger className="h-8 w-[100px]">
          <SelectValue placeholder={labels.filterSelect} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="1">{labels.filterYes}</SelectItem>
          <SelectItem value="0">{labels.filterNo}</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  // Select variant with pre-fetched options
  if (variant === 'select' && selectOptions && selectOptions.length > 0) {
    return (
      <SelectValueInput
        value={row.value != null ? String(row.value) : ''}
        options={selectOptions}
        onChange={(v) => onChange(v)}
        placeholder={labels.filterSelect}
      />
    );
  }

  if (variant === 'number') {
    return (
      <DebouncedInput
        value={row.value != null ? String(row.value) : ''}
        onChange={(v) => onChange(v)}
        type="number"
      />
    );
  }

  // Default: text input (covers text, date as string, select without options)
  return (
    <DebouncedInput
      value={row.value != null ? String(row.value) : ''}
      onChange={(v) => onChange(v)}
      type="text"
      placeholder={labels.filterValue}
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdvancedFilterRowComponent({
  row,
  index,
  columns,
  onChange,
  onRemove,
  labels,
  selectOptions,
}: AdvancedFilterRowProps) {
  const l = { ...DEFAULT_ADVANCED_TABLE_LABELS, ...labels };
  const [fieldOpen, setFieldOpen] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const selectedColumn = columns.find((c) => c.fieldname === row.field);
  const variant = getFilterVariant(selectedColumn?.type);
  const operators = getOperatorsForVariant(variant);

  const handleFieldChange = (fieldname: string) => {
    const col = columns.find((c) => c.fieldname === fieldname);
    const newVariant = getFilterVariant(col?.type);
    const newOps = getOperatorsForVariant(newVariant);
    const defaultOp = newOps[0]?.value ?? 'equals';
    onChange(row.id, { field: fieldname, operator: defaultOp, value: null });
    setFieldOpen(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 rounded-xs',
        isDragging && 'opacity-50 bg-accent/30',
      )}
    >
      {/* Drag handle */}
      <button
        type="button"
        className="cursor-grab touch-none text-muted-foreground/60 hover:text-muted-foreground"
        aria-label="Reorder filter"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Join label */}
      <span className="w-[52px] shrink-0 text-xs font-medium text-muted-foreground text-right">
        {index === 0 ? l.filterWhere : l.filterAnd}
      </span>

      {/* Field selector */}
      <Popover open={fieldOpen} onOpenChange={setFieldOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={fieldOpen}
            className="h-8 w-[160px] justify-between text-xs font-normal"
          >
            <span className="truncate">
              {selectedColumn?.label ?? selectedColumn?.fieldname ?? l.filterSelectField}
            </span>
            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0" align="start">
          <Command>
            <CommandInput placeholder={l.filterSearchFields} className="h-8" />
            <CommandList>
              <CommandEmpty>{l.filterNoFields}</CommandEmpty>
              <CommandGroup>
                {columns.map((col) => (
                  <CommandItem
                    key={col.fieldname}
                    value={col.label ?? col.fieldname}
                    onSelect={() => handleFieldChange(col.fieldname)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-3 w-3',
                        row.field === col.fieldname ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {col.label ?? col.fieldname}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Operator selector */}
      <Select
        value={row.operator}
        onValueChange={(op) =>
          onChange(row.id, { operator: op as FilterOperator, value: null })
        }
        disabled={!row.field}
      >
        <SelectTrigger className="h-8 w-[140px] text-xs">
          <SelectValue placeholder={l.filterOperator} />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op.value} value={op.value}>
              {l.operatorLabels[op.value] ?? op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value input */}
      {row.field && (
        <ValueInput
          row={row}
          column={selectedColumn}
          onChange={(value) => onChange(row.id, { value })}
          labels={l}
          selectOptions={selectOptions?.[row.field]}
        />
      )}

      {/* Delete button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
        onClick={() => onRemove(row.id)}
        aria-label="Remove filter"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
